/**
 * Mapea códigos de invite/accept/reject a copy friendly (nunca PostgREST raw).
 * Preserva el código en el Error.message interno vía mapInviteErrorCode si hace falta Sentry.
 */

const INVITE_CODE_MESSAGES: Record<string, string> = {
  not_authenticated: 'Sesión requerida. Volvé a iniciar sesión.',
  auth_user_mismatch: 'La sesión no coincide. Cerrá sesión y volvé a entrar con la cuenta correcta.',
  token_required: 'Falta el token de la invitación.',
  invite_not_found: 'No encontramos esa invitación. Pedile al dueño que te reenvíe.',
  invite_expired: 'La invitación venció. Pedile al dueño que te reenvíe.',
  invite_used: 'Esta invitación ya fue utilizada.',
  invite_cancelled: 'Esta invitación fue cancelada.',
  email_mismatch: 'Esta invitación es para otro correo. Iniciá sesión con ese correo.',
  owner_cannot_accept: 'Esta cuenta es dueña de un negocio. Usá otra cuenta para entrar como empleado.',
  already_employee_same_org:
    'Ya sos empleado de este negocio. Si te suman sucursales, pedí una invitación de extensión.',
  extension_wrong_user: 'Iniciá sesión con la cuenta de empleado que ya usás en Vuelto.',
  employee_not_found: 'No encontramos tu perfil de empleado.',
  no_branches: 'La invitación no tiene sucursales asignadas.',
  accept_failed: 'No pudimos completar la invitación. Reintentá en un momento.',
  reject_failed: 'No pudimos rechazar la invitación. Reintentá en un momento.',
  already_accepted: 'La invitación ya estaba completada.',
  permission_denied: 'No tenés permiso para gestionar invitaciones.',
  quota_exceeded: 'Alcanzaste el cupo de usuarios de tu plan. Mejorá el plan o liberá invitaciones pendientes.',
  plan_no_employees: 'Tu plan Gratis no incluye empleados. Mejorá el plan en Plan y facturación.',
  cancel_not_pending: 'Solo podés cancelar invitaciones pendientes.',
  resend_not_allowed: 'Solo podés reenviar invitaciones pendientes o canceladas.',
  email_send_failed: 'Generamos el link pero no pudimos enviar el correo. Copiá el link desde Invitaciones.',
  invalid_email: 'Ingresá un correo válido.',
  invalid_json: 'Pedido inválido. Reintentá.',
};

const RAW_LOOKS_INTERNAL =
  /^(PGRST|JWT|JSON object|duplicate key|violates|permission denied for|column |relation |function )/i;

export function mapInviteError(
  input: { code?: string | null; message?: string | null; email?: string | null } | string | null | undefined,
  fallback: string
): string {
  if (input == null) return fallback;

  if (typeof input === 'string') {
    return mapInviteErrorMessage(input, fallback);
  }

  const code = typeof input.code === 'string' ? input.code.trim() : '';
  if (code === 'email_mismatch' && input.email) {
    return `Esta invitación es para ${input.email}. Iniciá sesión con ese correo.`;
  }
  if (code && INVITE_CODE_MESSAGES[code]) {
    return INVITE_CODE_MESSAGES[code];
  }

  return mapInviteErrorMessage(input.message, fallback);
}

function mapInviteErrorMessage(raw: string | null | undefined, fallback: string): string {
  const msg = (raw ?? '').trim();
  if (!msg) return fallback;

  // Known Spanish messages already friendly (Edge legacy / quota copy).
  for (const [code, friendly] of Object.entries(INVITE_CODE_MESSAGES)) {
    if (msg === friendly) return friendly;
    if (new RegExp(code, 'i').test(msg) && INVITE_CODE_MESSAGES[code]) {
      return INVITE_CODE_MESSAGES[code];
    }
  }

  if (/ya tiene cuenta|already been registered|already exists/i.test(msg)) {
    return 'Ese correo ya tiene cuenta. Iniciá sesión con Google, Apple o correo.';
  }
  if (/cupo|máximo de usuarios|quota/i.test(msg)) {
    return INVITE_CODE_MESSAGES.quota_exceeded;
  }
  if (/plan Gratis|no incluye empleados/i.test(msg)) {
    return INVITE_CODE_MESSAGES.plan_no_employees;
  }
  if (/permiso.*invit/i.test(msg)) {
    return INVITE_CODE_MESSAGES.permission_denied;
  }
  if (RAW_LOOKS_INTERNAL.test(msg)) {
    return fallback;
  }

  // Short Spanish sentence from our own Edge — keep it.
  if (/[áéíóúñ]/i.test(msg) || /\b(no |la |el |tu |esta )/i.test(msg)) {
    if (msg.length <= 180) return msg;
  }

  return fallback;
}
